import { useEffect, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DictType } from "@pma/types";
import { Badge } from "@components/common/badge";
import DeleteTask from "./delete-task";
import { Button } from "@components/common/button";
import { GripVertical } from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@components/common/card";
import TaskEditor from "./task-editor";
import useTranslator from "@hooks/use-translator";
import { cn, formatStringWithLink } from "@pma/utils/helpers";
import ExpandTask from "./expand-task";

interface Props {
  task: DictType;
  deleteTask: (id: string) => void;
  updateTask: (id: string, content: string) => void;
}

function UsersTaskCard({ task, deleteTask, updateTask }: Props) {
  const [mouseIsOver, setMouseIsOver] = useState(false);
  const [editMode, setEditMode] = useState(true);

  const { locale } = useTranslator();

  const isHebrew = locale === "he";

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id:  task.uqId,
    data: {
      type: "Task",
      task,
    },
  });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
    setMouseIsOver(false);
  };


  if (isDragging ) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="relative flex h-[100px] min-h-[100px] cursor-grab items-center rounded-xl border-2 border-primary bg-slate-200 p-2.5 text-left opacity-30"
      />
    );
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      onClick={toggleEditMode}
      className="hover:ring-2 hover:ring-inset hover:primary/40"
      onMouseEnter={() => {
        setMouseIsOver(true);
      }}
      onMouseLeave={() => {
        setMouseIsOver(false);
      }}
    >
      <CardHeader className="space-between relative flex flex-row border-b-[1px] border-primary/20 px-3 py-3">
        <Button
          variant={"ghost"}
          {...attributes}
          {...listeners}
          className="-ml-2 h-auto cursor-grab p-1 text-primary/50"
        >
          <span className="sr-only">Move task</span>
          <GripVertical />
        </Button>
        <Badge
          style={{
            background: task?.status?.color,
          }}
          className={cn(
            "h-4 min-w-14 rounded-full flex items-center justify-center text-[10px] absolute top-1 ",
            isHebrew ? "left-2" : "right-2",
          )}
        >
          {task?.status?.name}
        </Badge>
      </CardHeader>

      <CardContent className="task relative flex min-h-fit items-center p-1 text-left overflow-hidden">
        <div className="my-auto flex flex-col justify-start h-full">
          <TaskEditor id={task?.id} title={"title"} value={task?.title}>
            <div
              className="font-semibold min-h-fit text-left text-wrap pb-2 w-full overflow-y-auto overflow-x-hidden whitespace-pre-wrap text-sm"
              dangerouslySetInnerHTML={{
                __html: formatStringWithLink(task?.title),
              }}
            />
          </TaskEditor>

          <TaskEditor
            id={task?.id}
            title={"description"}
            value={task?.description}
          >
            <p className="font-normal  w-full overflow-y-auto overflow-x-hidden whitespace-pre-wrap text-sm">
              {task?.dec ? (
                <div
                  className="w-fit text-left"
                  dangerouslySetInnerHTML={{
                    __html: formatStringWithLink(task?.dec),
                  }}
                />
              ) : (
                "---"
              )}
            </p>
          </TaskEditor>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end items-center p-0 rounded-b-md">
        <ExpandTask task={task} />
        <DeleteTask task={task} />
      </CardFooter>
    </Card>
  );
}

export default UsersTaskCard;
